import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { finalize } from 'rxjs';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { RulesApiService } from 'src/app/core/services/rules-api.service';
import { DeleteConfirmModalComponent } from 'src/app/shared/components/delete-confirm-modal/delete-confirm-modal.component';
import { EntityCatalogComponent } from 'src/app/shared/components/entity-catalog/entity-catalog.component';
import { entityText } from 'src/app/shared/components/entity-catalog/entity-text.util';
import {
  buildDeletedCatalog,
  formatDeletedAt,
} from 'src/app/shared/components/entity-catalog/entity-catalog-deleted.util';
import { EntityCatalog, EntityRecord } from 'src/app/shared/components/entity-catalog/entity-catalog.types';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { RestoreConfirmModalComponent } from 'src/app/shared/components/restore-confirm-modal/restore-confirm-modal.component';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { DOCUMENT_TYPE_CATALOG } from './document-types.catalog';
import { toSelectableCountryPackOptions } from '../country-packs/country-pack-options';

type DocumentTypeView = 'active' | 'deleted';

function buildActiveCatalog(
  countryPackOptions: ReturnType<typeof toSelectableCountryPackOptions>,
): EntityCatalog {
  return {
    ...DOCUMENT_TYPE_CATALOG,
    fields: DOCUMENT_TYPE_CATALOG.fields.map((field) =>
      field.key === 'country_pack' ? { ...field, options: countryPackOptions } : field,
    ),
  };
}

@Component({
  selector: 'app-document-types',
  standalone: true,
  imports: [
    CommonModule,
    EntityCatalogComponent,
    PageSurfaceHeaderComponent,
    IconModule,
    DeleteConfirmModalComponent,
    RestoreConfirmModalComponent,
  ],
  templateUrl: './document-types.component.html',
})
export class DocumentTypesComponent implements OnInit {
  private readonly api = inject(RulesApiService);

  activeCatalog: EntityCatalog = DOCUMENT_TYPE_CATALOG;
  readonly deletedCatalog = buildDeletedCatalog(DOCUMENT_TYPE_CATALOG);
  readonly toolbarTabs = [{ key: 'deleted', label: 'Deleted' }];

  view: DocumentTypeView = 'active';
  records: EntityRecord[] = [];
  loading = false;
  saving = false;
  syncing = false;
  error = '';
  notice = '';

  showDeleteModal = false;
  deleteError = '';
  readonly deleteLoading = signal(false);
  selectedForDelete: EntityRecord | null = null;

  showRestoreModal = false;
  readonly restoreLoading = signal(false);
  selectedForRestore: EntityRecord | null = null;

  ngOnInit(): void {
    this.loadCountryPacks();
    this.load();
  }

  get catalogView(): EntityCatalog {
    return this.view === 'deleted' ? this.deletedCatalog : this.activeCatalog;
  }

  get activeToolbarTab(): string {
    return this.view === 'deleted' ? 'deleted' : '';
  }

  onToolbarTabChange(key: string): void {
    this.view = this.view === key ? 'active' : (key as DocumentTypeView);
    this.error = '';
    this.load();
  }

  private loadCountryPacks(): void {
    this.api
      .listCountryPackRecords()
      .pipe(assertSuccessEnvelope())
      .subscribe({
        next: (res) => {
          this.activeCatalog = buildActiveCatalog(toSelectableCountryPackOptions(res.data ?? []));
        },
        error: () => {
          this.activeCatalog = buildActiveCatalog([]);
        },
      });
  }

  load(): void {
    this.loading = true;
    this.error = '';
    const request$ =
      this.view === 'deleted' ? this.api.listDeletedDocumentTypes() : this.api.listDocumentTypes();

    request$
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (res) => {
          const rows = (res.data ?? []) as EntityRecord[];
          this.records =
            this.view === 'deleted'
              ? rows.map((row) => ({ ...row, deleted_at: formatDeletedAt(row['deleted_at']) }))
              : rows;
        },
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  onSave(event: { record: EntityRecord; isNew: boolean }): void {
    const body = this.toRequestBody(event.record);
    const request$ = event.isNew
      ? this.api.createDocumentType(body)
      : this.api.updateDocumentType(String(event.record['code']), body);

    this.saving = true;
    this.error = '';
    request$
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: () => {
          this.notice = 'Saved. Run "Sync to tenants" to push this to tenant databases.';
          this.load();
        },
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  onDelete(row: EntityRecord): void {
    this.deleteError = '';
    this.selectedForDelete = row;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selectedForDelete = null;
    this.deleteError = '';
  }

  submitDelete(): void {
    const row = this.selectedForDelete;
    if (!row) return;

    const code = entityText(row['code']).trim();
    if (!code) return;

    this.deleteLoading.set(true);
    this.deleteError = '';
    this.api
      .deleteDocumentType(code)
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => this.deleteLoading.set(false)),
      )
      .subscribe({
        next: () => {
          this.closeDeleteModal();
          this.notice = 'Deleted. Run "Sync to tenants" to remove it from tenant databases.';
          this.load();
        },
        error: (err) => (this.deleteError = resolveApiErrorMessage(err)),
      });
  }

  onRestore(row: EntityRecord): void {
    this.selectedForRestore = row;
    this.showRestoreModal = true;
  }

  closeRestoreModal(): void {
    this.showRestoreModal = false;
    this.selectedForRestore = null;
  }

  submitRestore(): void {
    const row = this.selectedForRestore;
    if (!row) return;

    const code = entityText(row['code']).trim();
    if (!code) return;

    this.restoreLoading.set(true);
    this.error = '';
    this.api
      .restoreDocumentType(code)
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => this.restoreLoading.set(false)),
      )
      .subscribe({
        next: () => {
          this.closeRestoreModal();
          this.view = 'active';
          this.notice = 'Restored. Run "Sync to tenants" to push this to tenant databases.';
          this.load();
        },
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  get deleteEntityName(): string {
    const row = this.selectedForDelete;
    if (!row) return '';
    const name = entityText(row['display_name']).trim();
    const code = entityText(row['code']).trim();
    return name ? `${code} — ${name}` : code;
  }

  get restoreEntityName(): string {
    const row = this.selectedForRestore;
    if (!row) return '';
    const name = entityText(row['display_name']).trim();
    const code = entityText(row['code']).trim();
    return name ? `${code} — ${name}` : code;
  }

  sync(): void {
    this.syncing = true;
    this.error = '';
    this.notice = '';
    this.api
      .syncDocumentTypes()
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => (this.syncing = false)),
      )
      .subscribe({
        next: (res) => (this.notice = res.message || 'Synced to tenants'),
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  private toRequestBody(record: EntityRecord): Record<string, unknown> {
    const body: Record<string, unknown> = {};
    for (const field of this.activeCatalog.fields) {
      const value = record[field.key];
      if (value === '' || value == null) continue;
      body[field.key] = value;
    }
    return body;
  }
}
